import { useAuthService } from '@/features/AuthForm/api';
import type { LocalState } from '@/features/AuthForm/model/types/auth';
import { debounce } from '@/shared/utils/debounce';
import { useUserStore } from '@/entities/user/index';
import { useModalsStore } from '@/app/store/modals';

export const useAuth = () => {
  const authService = useAuthService();
  const userStore = useUserStore();
  const { setModalState } = useModalsStore();
  const { t } = useI18n();

  const localState = reactive<LocalState>({
    loginValue: '',
    codeValue: '',
    isConfirmationCode: false,
    secretToken: '',
    isResendCode: false,
    isShowTimer: false,
    isDisabled: false,
    timer: 0,
    timerId: null,
    errorCode: 0,
  });

  const CODE_VALUE_LENGTH = 6;
  const PHONE_VALUE_LENGTH = 17;

  const isDisabledLoginButton: Ref<boolean> = computed(() => (
    localState.loginValue.length < PHONE_VALUE_LENGTH
    || localState.errorCode !== 0
    || localState.isDisabled
  ));

  const authTitle: Ref<string> = computed(() =>
    localState.isConfirmationCode
      ? t('enterCode')
      : t('authFormTitle'),
  );

  const authSubtitle: Ref<string> = computed(() =>
    localState.isConfirmationCode
      ? `${t('sendedCodeMessage')} <span class="auth-form__phone">${localState.loginValue}</span>`
      : t('codeMessage'),
  );

  const timerText: Ref<string> = computed(() => (
    `${t('newCodeMessage')} ${localState.timer} ${t('seconds')}`
  ));

  const errors: Ref<Record<number, string>> = computed(() => ({
    401:
      localState.loginValue !== ''
        ? t('wrongCodeMessage')
        : t('failedAuthMessage'),
    429: t('waitOneMinuteMessage'),
  }));

  const codeButtonText: Ref<string> = computed(() =>
    localState.isResendCode ? t('getNewCode') : t('getCode'),
  );

  const buttonText: Ref<string> = computed(() =>
    localState.isConfirmationCode && !localState.isResendCode ? t('login') : codeButtonText.value,
  );

  const decrementTimer = (): void => {
    if (localState.timer) {
      localState.timer -= 1;
    } else {
      clearInterval(localState.timerId as ReturnType<typeof setInterval>);
      localState.isResendCode = true;
      localState.isShowTimer = false;
    }
  };

  const startTimer = (): void => {
    localState.timer = 59;
    localState.isConfirmationCode = true;
    localState.isShowTimer = true;
    localState.errorCode = 0;
    localState.timerId = setInterval(decrementTimer, 1000);
  };

  const resetErrorCode = (): void => {
    localState.errorCode = 0;
  };

  const formatLogin = (value: string): string => value.replace(/[\s-]/g, '');

  const getCode = async (): Promise<void> => {
    try {
      localState.isDisabled = true;
      const { data: { secret } }: any = await authService.getCode({
        phone_number: formatLogin(localState.loginValue),
      });
      localState.codeValue = '';
      localState.secretToken = secret;
      startTimer();
    } catch (error: any) {
      localState.errorCode = error.status;
    }
  };

  const sign = debounce(async (): Promise<void> => {
    if (localState.codeValue.length !== CODE_VALUE_LENGTH) return;

    try {
      localState.errorCode = 0;
      const { data: { access, refresh, user } }: any = await authService.login({
        phone_number: formatLogin(localState.loginValue),
        secret: localState.secretToken,
        otp: localState.codeValue,
      });
      useCookie('ECOM_ACCESS_TOKEN', { path: '/', maxAge: 900 }).value = access;
      useCookie('ECOM_REFRESH_TOKEN', { path: '/', maxAge: 604800 }).value = refresh;
      setModalState('auth', false);
      userStore.setUser(user);
      userStore.setIsAuth(true);
    } catch (error: any) {
      localState.errorCode = error.status;
    }
  }, 500);

  const submitHandler = async (): Promise<void> => {
    await getCode();
  };

  watch(() => localState.codeValue, () => {
    if (localState.codeValue.length === CODE_VALUE_LENGTH) {
      sign();
    } if (localState.errorCode) {
      resetErrorCode();
    }
  }, { immediate: true });

  return {
    authTitle,
    authSubtitle,
    timerText,
    buttonText,
    errors,
    isDisabledLoginButton,
    resetErrorCode,
    getCode,
    sign,
    submitHandler,
    ...toRefs(localState),
  };
};
